/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query } from "firebase/firestore";
import { useOutletContext } from "react-router-dom";
import { db } from "../firebase";
import "./ParentMobile.css";

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="17" rx="3" />
      <path d="M8 2v4M16 2v4M3 10h18" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 6l12 12M6 18L18 6" />
    </svg>
  );
}

function LateIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v5l3 2" />
    </svg>
  );
}

function SparkIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3z" />
    </svg>
  );
}

function TrendIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 17l6-6 4 4 7-8" />
      <path d="M14 7h6v6" />
    </svg>
  );
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toLocalDayKey(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function normalizeDayKey(value) {
  if (!value) return "";
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value.slice(0, 10) : toLocalDayKey(d);
  }
  if (typeof value?.toDate === "function") return toLocalDayKey(value.toDate());
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : toLocalDayKey(d);
}

function formatMonthTitle(date) {
  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function formatCheckIn(value) {
  if (!value) return "--:--";
  if (typeof value === "string" && value.includes(":")) return value;
  const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return "--:--";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function sameLocalDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function prettyStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "present") return "Present";
  if (s === "late") return "Late";
  if (s === "absent") return "Absent";
  if (s === "holiday") return "Holiday";
  return "No mark";
}


function isDateWithinHoliday(dateKey, holiday) {
  if (!dateKey || !holiday) return false;
  const start = String(holiday.startDate || "");
  const end = String(holiday.endDate || holiday.startDate || "");
  return start && end && dateKey >= start && dateKey <= end;
}

function getSchoolHolidayForDate(dateKey, holidays) {
  return holidays.find((h) => isDateWithinHoliday(dateKey, h)) || null;
}

function niceHolidayType(type) {
  return String(type || "school holiday")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildAiInsight(records) {
  if (!records.length) {
    return {
      title: "Not enough attendance data yet",
      body: "Once more attendance records are available, PresentPlus will highlight trends and changes here.",
      tone: "neutral",
    };
  }

  const normalized = records.map((r) => ({
    ...r,
    status: String(r.status || "").toLowerCase(),
    dateKey: normalizeDayKey(r.dateKey || r.dayKey || r.date || r.createdAt || r.timestamp),
  }));

  const recent14 = normalized.slice(0, 14);
  const previous14 = normalized.slice(14, 28);
  const recentPresent = recent14.filter((r) => r.status === "present").length;
  const previousPresent = previous14.filter((r) => r.status === "present").length;
  const recentRate = recent14.length ? recentPresent / recent14.length : null;
  const previousRate = previous14.length ? previousPresent / previous14.length : null;
  const recentLate = recent14.filter((r) => r.status === "late").length;
  const recentAbsent = recent14.filter((r) => r.status === "absent").length;

  if (recentRate != null && previousRate != null && previous14.length >= 4) {
    const diff = recentRate - previousRate;
    if (diff <= -0.15) {
      return {
        title: "Attendance dropped this period",
        body: `Attendance is lower than the previous period, with ${recentLate} late marks and ${recentAbsent} absences recently.`,
        tone: "warning",
      };
    }
    if (diff >= 0.15) {
      return {
        title: "Attendance is improving",
        body: "Recent attendance is stronger than the previous period. This is a positive trend.",
        tone: "positive",
      };
    }
  }

  if (recentLate >= 2) {
    return {
      title: "Late arrivals are showing up",
      body: `There have been ${recentLate} late marks recently. It may help to keep an eye on morning arrival consistency.`,
      tone: "warning",
    };
  }

  if (recentAbsent >= 2) {
    return {
      title: "Absences need attention",
      body: `There have been ${recentAbsent} absences recently. Check the attendance pattern and school updates for context.`,
      tone: "warning",
    };
  }

  return {
    title: "Attendance is steady",
    body: "Recent records look stable with no major change in pattern.",
    tone: "neutral",
  };
}

export default function ParentAttendance() {
  const outlet = useOutletContext() || {};
  const { profile, activeChild, activeChildId } = outlet;

  const schoolId = profile?.schoolId || "";
  const childName = activeChild?.name || "Child attendance";
  const childYear = activeChild?.year || activeChild?.yearGroup || activeChild?.className || "";

  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [schoolHolidays, setSchoolHolidays] = useState([]);
  const [bankHolidays, setBankHolidays] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDayKey, setSelectedDayKey] = useState("");
const bankHolidayMap = useMemo(() => {
  return new Map(
    bankHolidays.map((item) => [
      String(item.date),
      item.title,
    ])
  );
}, [bankHolidays]);

  useEffect(() => {
    if (!schoolId || !activeChildId) {
      setRecords([]);
      setSelectedDayKey("");
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const q = query(
      collection(db, "schools", schoolId, "students", activeChildId, "attendance_daily")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...(d.data() || {}) }));

        rows.sort((a, b) => {
          const aKey = normalizeDayKey(a.dateKey || a.dayKey || a.date || a.createdAt || a.timestamp);
          const bKey = normalizeDayKey(b.dateKey || b.dayKey || b.date || b.createdAt || b.timestamp);
          return bKey.localeCompare(aKey);
        });

        setRecords(rows);

        const todayKey = toLocalDayKey(new Date());

        // Keep the calendar focused on today's date by default.
        // Parents can still tap any other day to view its details,
        // but the blue calendar circle should not move away from today.
        setSelectedDayKey((prev) => prev || todayKey);
        setLoading(false);
      },
      (error) => {
        console.error("Attendance subscription failed:", error);
        setRecords([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [schoolId, activeChildId]);

  useEffect(() => {
    if (!schoolId) {
      setSchoolHolidays([]);
      return undefined;
    }

    const q = query(collection(db, "schools", schoolId, "schoolHolidays"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...(d.data() || {}) }));
        setSchoolHolidays(rows);
      },
      (error) => {
        console.error("School holidays subscription failed:", error);
        setSchoolHolidays([]);
      }
    );

    return () => unsub();
  }, [schoolId]);

  useEffect(() => {
  let alive = true;

  async function loadBankHolidays() {
    try {
      const res = await fetch("https://www.gov.uk/bank-holidays.json");
      const data = await res.json();

      const events = data?.["england-and-wales"]?.events || [];

      if (!alive) return;

      setBankHolidays(
        events.map((item) => ({
          date: item.date,
          title: item.title,
        }))
      );
    } catch (error) {
      console.warn("Bank holidays failed to load:", error);
      setBankHolidays([]);
    }
  }

  loadBankHolidays();

  return () => {
    alive = false;
  };
}, []);

  const summary = useMemo(() => {
    const normalized = records.map((r) => {
      const dateKey = normalizeDayKey(r.dateKey || r.dayKey || r.date || r.createdAt || r.timestamp);
      const status = String(r.status || "").toLowerCase();
      return { ...r, dateKey, status };
    });

    const presentCount = normalized.filter((r) => r.status === "present").length;
    const absentCount = normalized.filter((r) => r.status === "absent").length;
    const lateCount = normalized.filter((r) => r.status === "late").length;
    const total = normalized.length;
    const rate = total ? ((presentCount / total) * 100).toFixed(2) : "0.00";

    const statusByDay = new Map();
    const recordByDay = new Map();
    const monthBuckets = new Map();

    normalized.forEach((r) => {
      if (!r.dateKey) return;
      statusByDay.set(r.dateKey, r.status);
      recordByDay.set(r.dateKey, r);

      const monthKey = r.dateKey.slice(0, 7);
      if (!monthBuckets.has(monthKey)) {
        monthBuckets.set(monthKey, { present: 0, absent: 0, late: 0, total: 0 });
      }

      const bucket = monthBuckets.get(monthKey);
      bucket.total += 1;
      if (r.status === "present") bucket.present += 1;
      if (r.status === "absent") bucket.absent += 1;
      if (r.status === "late") bucket.late += 1;
    });

    const trendData = Array.from(monthBuckets.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)
      .map(([monthKey, stats]) => ({
        monthKey,
        label: new Date(`${monthKey}-01T00:00:00`).toLocaleDateString(undefined, { month: "short" }),
        rate: stats.total ? Math.round((stats.present / stats.total) * 100) : 0,
      }));

    return { total, rate, presentCount, absentCount, lateCount, statusByDay, recordByDay, trendData };
  }, [records]);

  const calendarMeta = useMemo(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const today = new Date();

    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastOfMonth.getDate();
    const firstDayIndex = firstOfMonth.getDay();

    const cells = [];

    for (let i = 0; i < firstDayIndex; i += 1) {
      cells.push({ key: `blank-${year}-${month + 1}-${i}`, blank: true });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, month, day);
      const dateKey = toLocalDayKey(date);
      const rawStatus = summary.statusByDay.get(dateKey) || "";

      const schoolHoliday = getSchoolHolidayForDate(dateKey, schoolHolidays);
      const bankHolidayName = bankHolidayMap.get(dateKey) || "";
      const holidayName = schoolHoliday?.title || bankHolidayName || "";
      const holidayType = schoolHoliday?.type || (bankHolidayName ? "bank_holiday" : "");

      let status = rawStatus;
      if (holidayName) status = "holiday";

      cells.push({
        key: `day-${year}-${month + 1}-${day}`,
        dateKey,
        day,
        status,
        holidayName,
        holidayType,
        isToday: sameLocalDay(date, today),
        isSelected: selectedDayKey === dateKey,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        blank: false,
      });
    }

    return { title: formatMonthTitle(calendarDate), cells };
  }, [calendarDate, summary.statusByDay, selectedDayKey, schoolHolidays]);

  const selectedRecord = useMemo(() => {
    if (!selectedDayKey) return null;
    return summary.recordByDay.get(selectedDayKey) || null;
  }, [selectedDayKey, summary.recordByDay]);

  const selectedMeta = useMemo(() => {
    if (!selectedDayKey) return null;

    const schoolHoliday = getSchoolHolidayForDate(selectedDayKey, schoolHolidays);
    const bankHoliday = bankHolidayMap.get(selectedDayKey);

    if (schoolHoliday || bankHoliday) {
      return {
        type: "holiday",
        title: schoolHoliday?.title || "School closed",
        subtitle: schoolHoliday
          ? `${niceHolidayType(schoolHoliday.type)} • School closed`
          : bankHoliday,
        icon: "🌿",
      };
    }

    if (!selectedRecord) {
      return {
        type: "empty",
        title: "No attendance mark",
        subtitle: "No record posted for this day",
        icon: "—",
      };
    }

    const status = String(selectedRecord.status || "").toLowerCase();

    if (status === "present") {
      return { type: "present", title: "Present", subtitle: "Marked present", icon: "✓" };
    }

    if (status === "late") {
      return { type: "late", title: "Late", subtitle: "Arrived late", icon: "⏱" };
    }

    if (status === "absent") {
      return { type: "absent", title: "Absent", subtitle: "Marked absent", icon: "✕" };
    }

    return {
      type: "empty",
      title: "No attendance mark",
      subtitle: "No record posted for this day",
      icon: "—",
    };
  }, [selectedDayKey, selectedRecord, schoolHolidays]);

  const recentHistory = records.slice(0, 6);
  const aiInsight = useMemo(() => buildAiInsight(records), [records]);
  const maxTrend = Math.max(...summary.trendData.map((d) => d.rate), 1);

  function goMonth(offset) {
    setCalendarDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  }

  return (
    <div className="pp-parent-main-content">
      <section className="pp-card pp-attn-hero">
        <div className="pp-attn-hero-top">
          <div>
            <p className="pp-attn-eyebrow">Attendance overview</p>
            <h2 className="pp-section-title" style={{ marginBottom: 6 }}>
              {childName}
            </h2>
            <p className="pp-muted">
              {childYear ? `${childYear} • ` : ""}
              Based on real school attendance records
            </p>
          </div>

          <div className="pp-attn-rate-chip">
            <span className="pp-attn-rate-value">
              {loading ? "..." : `${summary.rate}%`}
            </span>
            <span className="pp-attn-rate-label">Attendance</span>
          </div>
        </div>

        <div className="pp-attn-stats">
          <div className="pp-attn-stat-card">
            <span className="pp-attn-stat-label">Present</span>
            <strong>{summary.presentCount}</strong>
          </div>
          <div className="pp-attn-stat-card">
            <span className="pp-attn-stat-label">Late</span>
            <strong>{summary.lateCount}</strong>
          </div>
          <div className="pp-attn-stat-card">
            <span className="pp-attn-stat-label">Absent</span>
            <strong>{summary.absentCount}</strong>
          </div>
        </div>
      </section>

<section className={`pp-card pp-ai-insight-card tone-${aiInsight.tone}`}>
  <div className="pp-ai-insight-layout">
    <span className="pp-ai-insight-icon">
      <SparkIcon />
    </span>

    <div className="pp-ai-insight-content">
      <p className="pp-attn-eyebrow">Attendance insight</p>

      <h2 className="pp-section-title pp-ai-insight-title">
        {aiInsight.title}
      </h2>

      <p className="pp-muted pp-ai-insight-body">
        {aiInsight.body}
      </p>
    </div>
  </div>
</section>

    <section className="pp-card pp-calendar-card">
<div className="pp-calendar-top v4">
<div className="pp-calendar-left">
  <div>
    <h2 className="pp-section-title pp-calendar-title-fixed">
      Attendance Calendar
    </h2>

    <div className="pp-calendar-sub-row">
      <span className="pp-calendar-icon-wrap small">
        <CalendarIcon />
      </span>

      <p className="pp-attn-eyebrow pp-calendar-subtitle-fixed">
        Monthly view
      </p>
    </div>
  </div>
</div>

  <div className="pp-calendar-controls v4">
            <button type="button" className="pp-calendar-nav-btn glass" onClick={() => goMonth(-1)} aria-label="Previous month">
              ‹
            </button>
            <div className="pp-calendar-month-pill">{calendarMeta.title}</div>
            <button type="button" className="pp-calendar-nav-btn glass" onClick={() => goMonth(1)} aria-label="Next month">
              ›
            </button>
          </div>
        </div>

        <div className="pp-calendar-weekdays">
          {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
            <span key={`${day}-${i}`}>{day}</span>
          ))}
        </div>

        <div className="pp-calendar-grid-v2">
          {calendarMeta.cells.map((cell) =>
            cell.blank ? (
              <div key={cell.key} className="pp-calendar-day blank" />
            ) : (
              <button
                key={cell.key}
                type="button"
className={[
  "pp-calendar-day",
  cell.status ? `status-${cell.status}` : "",
  cell.status === "holiday" ? "holiday" : "",
  cell.isWeekend ? "weekend" : "",
  cell.isToday ? "today selected" : "",
].filter(Boolean).join(" ")}
                onClick={() => setSelectedDayKey(cell.dateKey)}
                title={cell.holidayName || prettyStatus(cell.status)}
              >
                <span className="pp-calendar-day-num">{cell.day}</span>
              </button>
            )
          )}
        </div>

        <div className="pp-calendar-legend">
          <span className="legend-item present"><CheckIcon /> Present</span>
          <span className="legend-item late"><LateIcon /> Late</span>
          <span className="legend-item absent"><CloseIcon /> Absent</span>
          <span className="legend-item holiday">Holiday</span>
        </div>
      </section>

      <section className="pp-card pp-attendance-trend-card">
        <div className="pp-attendance-trend-top">
          <div>
            <p className="pp-attn-eyebrow">Monthly trend</p>
            <h2 className="pp-section-title" style={{ marginBottom: 0 }}>
              Attendance trend
            </h2>
          </div>
          <span className="pp-attendance-trend-icon"><TrendIcon /></span>
        </div>

        {summary.trendData.length ? (
          <div className="pp-trend-chart">
            {summary.trendData.map((item) => (
              <div key={item.monthKey} className="pp-trend-bar-col">
                <div className="pp-trend-bar-wrap">
                  <div
                    className="pp-trend-bar"
                    style={{ height: `${Math.max((item.rate / maxTrend) * 100, 10)}%` }}
                    title={`${item.label}: ${item.rate}%`}
                  />
                </div>
                <span className="pp-trend-bar-label">{item.label}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="pp-muted">Trend chart will appear as more attendance data comes in.</p>
        )}
      </section>

      <section className="pp-card pp-attn-detail">
        <div className="pp-attn-detail-head">
          <div className="pp-attn-detail-title-wrap">
            <span className="pp-attn-detail-icon">{selectedMeta?.icon || "—"}</span>
            <div>
              <h3 className="pp-attn-detail-title">
                {selectedMeta?.title || "No day selected"}
              </h3>
              <p className="pp-attn-detail-sub">{selectedMeta?.subtitle || ""}</p>
            </div>
          </div>

          <span className={`pp-status-pill ${selectedMeta?.type || ""}`}>
            {selectedMeta?.type === "holiday" ? "Holiday" : prettyStatus(selectedRecord?.status)}
          </span>
        </div>

        {selectedDayKey ? (
          <div className="pp-attn-detail-grid">
            <div className="pp-attn-detail-box">
              <span className="pp-attn-detail-label">Date</span>
              <strong>
                {new Date(`${selectedDayKey}T00:00:00`).toLocaleDateString(undefined, {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
              </strong>
            </div>

            {selectedMeta?.type !== "holiday" ? (
              <div className="pp-attn-detail-box">
                <span className="pp-attn-detail-label">Check-in</span>
                <strong>
                  {selectedRecord
                    ? formatCheckIn(selectedRecord.checkInTime || selectedRecord.time || selectedRecord.createdAt || selectedRecord.timestamp)
                    : "--:--"}
                </strong>
              </div>
            ) : (
              <div className="pp-attn-detail-box">
                <span className="pp-attn-detail-label">School status</span>
                <strong>Closed</strong>
              </div>
            )}
          </div>
        ) : null}
      </section>

      <section className="pp-card">
        <div className="pp-attn-detail-top">
          <h2 className="pp-section-title" style={{ marginBottom: 0 }}>
            Recent history
          </h2>
        </div>

        {loading ? (
          <p className="pp-muted">Loading attendance history...</p>
        ) : recentHistory.length ? (
          <div className="pp-history-list">
            {recentHistory.map((item, idx) => {
              const status = String(item.status || "").toLowerCase();
              const dateKey = normalizeDayKey(item.dateKey || item.dayKey || item.date || item.createdAt || item.timestamp);

              return (
                <div key={`${item.id}-${dateKey}-${idx}`} className={`pp-history-row ${idx !== recentHistory.length - 1 ? "bordered" : ""}`}>
                  <div className="pp-history-left">
                    <div className="pp-history-index">{idx + 1}</div>
                    <div>
                      <div className="pp-history-main">
                        {dateKey
                          ? new Date(`${dateKey}T00:00:00`).toLocaleDateString(undefined, {
                              weekday: "short",
                              day: "numeric",
                              month: "short",
                            })
                          : "Record"}
                      </div>
                      <div className="pp-history-sub">
                        {formatCheckIn(item.checkInTime || item.time || item.createdAt || item.timestamp)}
                      </div>
                    </div>
                  </div>

                  <div className={`pp-status-pill ${status}`}>
                    {prettyStatus(status)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="pp-muted">No attendance records yet for this child.</p>
        )}
      </section>
    </div>
  );
}